import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MousePointerSquareDashed, Plus, Trash2, X } from "lucide-react";
import { PanelSelect } from "../../PanelSelect";
import { RoomCircuitsBuilder } from "../RoomCircuitsBuilder";
import type { AssignModePanelProps } from "./types";

export const AssignModePanel = ({
  selection,
  groups,
  targets,
  boardFilter,
  splittingName,
  remainingCount,
  boardName,
  categoryName,
  canRemove,
  onAssignToCircuit,
  onNewCircuit,
  missingFeeders,
  onPickFeeder,
  onLeaveOut,
  onRemove,
  onClearSelection,
  onSelectKeys,
  onDone,
  categories,
  activeCategory,
  onPickCategory,
  roomCircuits,
}: AssignModePanelProps) => {
  const { t } = useTranslation();
  const [newBoard, setNewBoard] = useState<string | null>(null);

  const category = selection.categories[0] ?? null;
  const board = newBoard && targets.boards.includes(newBoard) ? newBoard : (targets.boards[0] ?? null);

  return (
    <section
      data-testid="assign-mode"
      className="flex min-h-[8rem] flex-1 flex-col overflow-hidden rounded-lg border border-blue-300 bg-white shadow-sm"
    >
      <div className="flex flex-none items-center gap-2 border-b border-blue-100 bg-blue-50 px-3 py-2">
        <MousePointerSquareDashed size={17} className="flex-none text-blue-600" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">
          {splittingName
            ? t("smartSurveyor.cablesBeta.assignModeTitleSplit", { circuit: splittingName })
            : boardFilter
              ? t("smartSurveyor.cablesBeta.assignModeTitleBoard", { board: boardName(boardFilter) })
              : t("smartSurveyor.cablesBeta.assignModeTitle")}
        </span>
        <button
          type="button"
          onClick={onDone}
          className="flex-none rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
        >
          {t("smartSurveyor.cablesBeta.assignModeDone")}
        </button>
      </div>
      <p className="flex-none px-3 pt-2 text-[12.5px] leading-snug text-slate-500">
        {t(
          splittingName
            ? "smartSurveyor.cablesBeta.assignModeHintSplit"
            : "smartSurveyor.cablesBeta.assignModeHint",
        )}
      </p>

      {categories.length > 0 && (
        <div
          role="radiogroup"
          aria-label={t("smartSurveyor.cablesBeta.assignCategoryPick")}
          data-testid="assign-categories"
          className="flex flex-none flex-wrap gap-1.5 px-3 pt-2"
        >
          {categories.map(({ category: shown, total, open }) => {
            const isActive = shown === activeCategory;
            return (
              <button
                key={shown}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => onPickCategory(shown)}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] font-medium ${
                  isActive
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"
                }`}
              >
                {categoryName(shown)}
                <span className={`tabular-nums ${isActive ? "text-blue-100" : "text-slate-500"}`}>{total}</span>
                {open > 0 && (
                  <span
                    className={`rounded-full px-1.5 text-[11.5px] font-semibold tabular-nums ${
                      isActive ? "bg-white text-blue-700" : "bg-amber-100 text-amber-800"
                    }`}
                    title={t("smartSurveyor.cablesBeta.assignCategoryOpen", { count: open })}
                  >
                    {t("smartSurveyor.cablesBeta.assignCategoryOpen", { count: open })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-2">
        {selection.count > 0 ? (
          <div data-testid="assign-selection" className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50/50 p-2.5">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 text-[14px] font-semibold text-slate-900">
                {t("smartSurveyor.cablesBeta.assignSelected", { count: selection.count })}
                {selection.categories.length > 0 && (
                  <span className="ml-1 font-normal text-slate-500">
                    · {selection.categories.map(categoryName).join(", ")}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={onClearSelection}
                aria-label={t("smartSurveyor.cablesBeta.assignClear")}
                title={t("smartSurveyor.cablesBeta.assignClear")}
                className="grid h-8 w-8 flex-none place-items-center rounded-md text-slate-500 hover:bg-white"
              >
                <X size={15} />
              </button>
            </div>

            {targets.circuits.length > 0 || board ? (
              <PanelSelect
                value=""
                options={targets.circuits.map((target) => ({
                  value: target.circuitId,
                  label: target.boardLabel
                    ? `${target.label} · ${boardName(target.boardLabel)}`
                    : target.label,
                  color: target.color,
                }))}
                placeholder={t("smartSurveyor.cablesBeta.assignOnto")}
                ariaLabel={t("smartSurveyor.cablesBeta.assignOnto")}
                onPick={onAssignToCircuit}
                {...(board
                  ? {
                      onCreate: (text: string) => onNewCircuit(board, text),
                      createLabel: (text: string) =>
                        t("smartSurveyor.cablesBeta.assignCreateNamed", {
                          name: text,
                          board: boardName(board),
                        }),
                    }
                  : {})}
              />
            ) : (
              <p className="text-[13px] text-amber-700">
                {category
                  ? t("smartSurveyor.cablesBeta.assignNoTarget", { category: categoryName(category) })
                  : t("smartSurveyor.cablesBeta.assignNoTargetAny")}
              </p>
            )}

            {board && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onNewCircuit(board)}
                  className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-2 text-[13.5px] font-medium text-blue-700 hover:bg-blue-50"
                >
                  <Plus size={15} className="flex-none" />
                  <span className="truncate">
                    {t("smartSurveyor.cablesBeta.assignNewCircuit", { board: boardName(board) })}
                  </span>
                </button>
                {targets.boards.length > 1 && (
                  <PanelSelect
                    value={board}
                    trigger="icon"
                    options={targets.boards.map((label) => ({ value: label, label: boardName(label) }))}
                    placeholder={t("smartSurveyor.cablesBeta.assignNewCircuitBoard")}
                    ariaLabel={t("smartSurveyor.cablesBeta.assignNewCircuitBoard")}
                    onPick={setNewBoard}
                  />
                )}
              </div>
            )}

            {canRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="flex items-center gap-1.5 self-start rounded px-1 text-[13px] text-slate-500 hover:text-red-600"
              >
                <Trash2 size={14} />
                {t("smartSurveyor.cablesBeta.assignRemove")}
              </button>
            )}
          </div>
        ) : splittingName ? (
          <p data-testid="assign-split-left" className="text-[13px] text-slate-600">
            {t("smartSurveyor.cablesBeta.assignSplitLeft", { count: remainingCount })}
          </p>
        ) : (
          <div data-testid="assign-unassigned" className="flex flex-col gap-3">
            {roomCircuits && <RoomCircuitsBuilder {...roomCircuits} />}
            {missingFeeders.length > 0 && (
              <div data-testid="assign-missing-feeders">
                <h3 className="mb-1 text-[13px] font-semibold text-slate-700">
                  {t("smartSurveyor.cablesBeta.assignMissingTitle")}
                </h3>
                <p className="mb-1.5 text-[12.5px] leading-snug text-slate-500">
                  {t("smartSurveyor.cablesBeta.assignMissingHint")}
                </p>
                <ul className="flex flex-col gap-1">
                  {missingFeeders.map(({ category: missing, count }) => (
                    <li
                      key={missing}
                      className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5"
                    >
                      <span className="min-w-0 flex-1 text-[13.5px] text-slate-800">
                        {t("smartSurveyor.cablesBeta.assignMissingRow", {
                          count,
                          category: categoryName(missing),
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => onPickFeeder(missing)}
                        className="flex-none rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-[13px] font-medium text-blue-700 hover:border-blue-400"
                      >
                        {t("smartSurveyor.cablesBeta.assignMissingPick")}
                      </button>
                      <button
                        type="button"
                        onClick={() => onLeaveOut(missing)}
                        className="flex-none rounded-md px-2 py-1.5 text-[13px] text-slate-600 hover:bg-white"
                      >
                        {t("smartSurveyor.cablesBeta.assignMissingLeaveOut")}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h3 className="mb-1.5 text-[13px] font-semibold text-slate-700">
                {t("smartSurveyor.cablesBeta.assignUnassignedTitle")}
              </h3>
              {groups.length === 0 ? (
                <p className="text-[13px] text-slate-500">{t("smartSurveyor.cablesBeta.assignNothingLeft")}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {groups.map((group) => (
                  <li
                    key={`${group.kind}|${group.name}`}
                    className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[13.5px] text-slate-800" title={group.name}>
                        {group.keys.length} × {group.name}
                      </span>
                      <span className="text-[12px] text-slate-500">
                        {t(
                          group.kind === "unfed"
                            ? "smartSurveyor.cablesBeta.assignGroupUnfed"
                            : "smartSurveyor.cablesBeta.assignGroupNoCircuit",
                        )}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onSelectKeys(group.keys)}
                      className="flex-none rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-[13px] font-medium text-blue-700 hover:border-blue-400"
                    >
                      {t("smartSurveyor.cablesBeta.assignGroupSelect")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
